// Lo que se lee de los recibos de CFE (la IA lo llena; el vendedor lo revisa y lo EDITA). Una LecturaRecibo por medidor,
// consolidada de todos sus archivos (un recibo doméstico trae el historial del año; en GDMTO/GDMTH se suben 12).
// Medido el 9-oct en recibos reales de ~/Downloads: el recibo GDMTH trae en la hoja 1 kWh base/intermedia/punta,
// kW por periodo, KWMax, kVArh, factor de potencia y el desglose (suministro, distribución, transmisión, CENACE,
// generación B/I/P, capacidad, SCnMEM, cargo fijo, 2 % baja tensión, bonificación FP, DAP, IVA); en la hoja 2 el
// CONSUMO HISTÓRICO trae por mes: demanda kW, consumo total kWh, factor de potencia, factor de carga y precio medio
// (sin reparto B/I/P ni importe). El doméstico (1E, 1F) trae bloques básico/intermedio/excedente y ApoyoEdoSon.
import { z } from 'zod';
import { Facturacion, Fecha, FechaHora, Id, Medidor, Pesos, Tarifa, Texto } from './basicos.ts';

export const OrigenPeriodo = z.enum([
  'recibo', // el periodo facturado de un recibo (con todo su detalle)
  'historial_recibo', // un renglón del historial impreso en un recibo
  'estimado', // estimado (cliente nuevo o meses que faltan)
  'manual', // lo tecleó el vendedor
]);
export type OrigenPeriodo = z.infer<typeof OrigenPeriodo>;

/** Desglose del periodo facturado (lo que se pueda leer; lo que no está, null). Montos en pesos. */
export const CargosPeriodo = z.strictObject({
  suministro: Pesos.nullable(),
  distribucion: Pesos.nullable(),
  transmision: Pesos.nullable(),
  cenace: Pesos.nullable(),
  generacion: Pesos.nullable(),
  capacidad: Pesos.nullable(),
  scnmem: Pesos.nullable(),
  cargo_fijo: Pesos.nullable(),
  energia: Pesos.nullable(),
  baja_tension_2pct: Pesos.nullable(),
  /** Negativa si es bonificación. */
  factor_potencia: z.number().nullable(),
  /** Derecho de alumbrado público. */
  dap: Pesos.nullable(),
  /** ApoyoEdoSon y similares (negativo = descuento). Nunca se imprime en la propuesta (ESPEC, anexo). */
  apoyo_estatal: z.number().nullable(),
  subtotal: Pesos.nullable(),
  iva: Pesos.nullable(),
  total_periodo: Pesos.nullable(),
});
export type CargosPeriodo = z.infer<typeof CargosPeriodo>;

/** Precio por kWh de cada bloque o franja del periodo facturado, como viene impreso. */
export const BloquePrecio = z.strictObject({
  bloque: z.enum(['basico', 'intermedio_1', 'intermedio_2', 'intermedio', 'excedente', 'base', 'intermedia', 'punta', 'unico']),
  kwh: z.number().nonnegative().nullable(),
  precio_kwh: z.number().nonnegative(),
});
export type BloquePrecio = z.infer<typeof BloquePrecio>;

/** z25 (Diego, 20-oct-2026): el consumo de este periodo lo AJUSTÓ el vendedor (con la IA o la IA simulada) porque el del
 *  recibo no representa el uso de hoy (casa en construcción, no vivían, equipos nuevos). Guarda lo que decía el recibo,
 *  para auditoría. Al ajustar, `importe` queda en null: lo que cobró CFE ya no es el cobro de ESTE consumo, así que el
 *  motor no lo compara contra las cuotas (no hay «lo que cobró CFE no cuadra» falso) y estima el pago con las cuotas. */
/** z25d (verificación del 20-oct, GDMTO con UN recibo): lo demás que el recibo traía en un periodo y que el ajuste pone en
 *  null o cambia (precio medio, factor de carga, kWh por franja, cargos, precios por bloque y su origen), como venía la
 *  PRIMERA vez. Sirve para dos cosas: el motor sabe que la demanda de ese mes es la MEDIDA (su origen era el recibo o su
 *  historial; editar los kWh no cambia la demanda), y «volver al consumo del recibo» deja el periodo idéntico al del
 *  recibo (antes se perdían el precio medio y el factor de carga y el pago del mes caía 60 %). `cargos` y
 *  `precios_energia` van solo si el periodo los traía (así se restaura también su ausencia). */
export const PeriodoDelRecibo = z.strictObject({
  origen: OrigenPeriodo,
  precio_medio: z.number().nonnegative().nullable(),
  factor_carga_pct: z.number().min(0).max(100).nullable(),
  kwh_base: z.number().nonnegative().nullable(),
  kwh_intermedia: z.number().nonnegative().nullable(),
  kwh_punta: z.number().nonnegative().nullable(),
  cargos: CargosPeriodo.nullable().optional(),
  precios_energia: z.array(BloquePrecio).max(12).optional(),
});
export type PeriodoDelRecibo = z.infer<typeof PeriodoDelRecibo>;

export const AjusteConsumoVendedor = z.strictObject({
  consumo_original_kwh: z.number().nonnegative(),
  importe_original: Pesos.nullable(),
  motivo: z.string().trim().min(1).max(400),
  fuente: z.enum(['ia', 'ia_simulada', 'vendedor']),
  /** z25d: lo demás del periodo del recibo (ver PeriodoDelRecibo). Ausente solo en un ajuste escrito a mano completo. */
  recibo: PeriodoDelRecibo.optional(),
});
export type AjusteConsumoVendedor = z.infer<typeof AjusteConsumoVendedor>;

/** Tanda 2 (Diego, 25-oct, «La Cacharamba», GDMTH con 11 recibos): el mes del año que no tiene su recibo lo PROYECTA la
 *  IA al terminar la lectura, y queda MARCADO «Proyectado con IA» en la tabla de Revisión. `base` «historial»: el mes traía
 *  su renglón del historial impreso (kWh y demanda REALES, que no se tocan) y la IA completó el reparto base/intermedia/punta
 *  y la demanda de punta; ese renglón sigue con origen «historial_recibo» (como hoy: sus kWh son de CFE y el PDF de varias
 *  sucursales lo imprime). «patron»: no traía nada y la IA proyectó todo con los otros meses; el renglón va con origen
 *  «estimado» (el motor y el PDF lo tratan como un mes estimado). Sin importe en ningún caso: el pago de ese mes lo estima el
 *  motor con las cuotas de CFE (nunca cuenta como recibo para «Pago hoy»). Opcional: lo guardado antes no lo trae. */
export const ProyeccionIA = z.strictObject({
  fuente: z.enum(['ia', 'ia_simulada']),
  base: z.enum(['historial', 'patron']),
  /** En una frase, para el vendedor: con qué meses se proyectó. */
  motivo: z.string().trim().min(1).max(400),
});
export type ProyeccionIA = z.infer<typeof ProyeccionIA>;

export const PeriodoConsumo = z.strictObject({
  inicio: Fecha.nullable(),
  fin: Fecha.nullable(),
  /** Como viene impreso ("MAR 26", "15 ENE 26-15 MAR 26"): sirve para revisar contra el recibo. */
  etiqueta: z.string().trim().min(1).max(40),
  consumo_kwh: z.number().nonnegative(),
  /** Lo que cobró CFE ese periodo (total del periodo, con IVA y DAP). null si el recibo no lo trae (historial GDMT). */
  importe: Pesos.nullable(),
  demanda_kw: z.number().nonnegative().nullable(),
  kwh_base: z.number().nonnegative().nullable(),
  kwh_intermedia: z.number().nonnegative().nullable(),
  kwh_punta: z.number().nonnegative().nullable(),
  factor_potencia_pct: z.number().min(0).max(100).nullable(),
  factor_carga_pct: z.number().min(0).max(100).nullable(),
  /** $/kWh promedio impreso en el historial GDMT. */
  precio_medio: z.number().nonnegative().nullable(),
  origen: OrigenPeriodo,
  // ---- Contrato v2 (opcionales): lo que trae CADA recibo de GDMTO/GDMTH en su hoja 1, para que el motor llene los
  // 12 periodos con cargos, demanda y cuotas (formato industrial, candado N9; formato horaria). Solo en renglones que
  // salen de un recibo (origen "recibo"); en renglones del historial impreso van ausentes o null.
  /** Días del periodo facturado, como los imprime el recibo. */
  dias: z.int().min(1).max(70).nullable().optional(),
  /** kW en punta del periodo (GDMTH). `demanda_kw` (arriba) es la que el historial imprime. */
  demanda_punta_kw: z.number().nonnegative().nullable().optional(),
  /** KWMax del periodo. */
  demanda_maxima_kw: z.number().nonnegative().nullable().optional(),
  /** Desglose de ESE periodo (como cargos_ultimo_periodo, pero por renglón). */
  cargos: CargosPeriodo.nullable().optional(),
  /** Precio por kWh de cada franja de ESE periodo, como viene impreso. */
  precios_energia: z.array(BloquePrecio).max(12).optional(),
  /** z25: presente si el vendedor ajustó el consumo (ver AjusteConsumoVendedor). */
  ajuste_vendedor: AjusteConsumoVendedor.nullable().optional(),
  /** Tanda 2: presente si el mes lo proyectó la IA (ver ProyeccionIA). */
  proyeccion_ia: ProyeccionIA.nullable().optional(),
  /** hoy86 · z64-2 (verificación «vendedor» r2): presente si los kWh de este periodo «estimado» los sugirió la IA con «Sugerir con
   *  IA» (no la curva de Sonora): el aviso del motor y el PDF lo dicen de la IA. null = ya no (el vendedor cambió el número). */
  sugerido_ia: z.enum(['ia', 'ia_simulada']).nullable().optional(),
});
export type PeriodoConsumo = z.infer<typeof PeriodoConsumo>;

/** Datos privados: NUNCA salen en el PDF; el generador truena si aparecen (N3, lista no_publicar). */
export const DatosPrivados = z.strictObject({
  titular: z.string().trim().max(160).nullable(),
  numero_servicio: z.string().trim().max(40).nullable(),
  direccion: z.string().trim().max(300).nullable(),
  rmu: z.string().trim().max(80).nullable(),
  cuenta: z.string().trim().max(40).nullable(),
  numero_medidor: z.string().trim().max(40).nullable(),
});
export type DatosPrivados = z.infer<typeof DatosPrivados>;

/** Etapa 4 (Diego, 12-oct, punto 7): quién AUTORIZÓ la tabla leída y cuándo. La pone SOLO el servidor (POST
 *  …/lecturas/:medidor/autorizar); una edición del vendedor no la puede escribir ni cambiar. */
export const AutorizacionLectura = z.strictObject({
  por: Id,
  nombre: Texto(1, 120),
  en: FechaHora,
});
export type AutorizacionLectura = z.infer<typeof AutorizacionLectura>;

export const EstadoLectura = z.enum(['sin_leer', 'leido', 'parcial', 'ilegible']);
export type EstadoLectura = z.infer<typeof EstadoLectura>;

export const LecturaRecibo = z.strictObject({
  medidor: Medidor,
  /** Archivos de los que salió esta lectura. */
  archivo_ids: z.array(Id),
  estado: EstadoLectura,
  privados: DatosPrivados,
  tarifa: Tarifa.nullable(),
  facturacion: Facturacion.nullable(),
  periodo_facturado: z.strictObject({ inicio: Fecha, fin: Fecha }).nullable(),
  total_a_pagar: Pesos.nullable(),
  demanda_contratada_kw: z.number().nonnegative().nullable(),
  carga_conectada_kw: z.number().nonnegative().nullable(),
  multiplicador: z.number().positive().nullable(),
  /** "NO HILOS" del frente del recibo: 2 = una fase y neutro, 3 = dos fases y neutro, 4 = trifásico. Decide si un
   *  inversor trifásico se conecta sin cambiar el servicio con CFE (hallazgo del 10-oct). Ausente/null = no se leyó. */
  hilos: z.int().min(2).max(4).nullable().optional(),
  /** Ordenado del más viejo al más nuevo, sin periodos repetidos. */
  historial: z.array(PeriodoConsumo).max(36),
  cargos_ultimo_periodo: CargosPeriodo.nullable(),
  precios_energia: z.array(BloquePrecio).max(12),
  /** JSON pointers (relativos a esta lectura) de los datos que la IA no leyó con seguridad: la interfaz los resalta. */
  campos_dudosos: z.array(z.string().regex(/^\//)),
  /** Lo que la IA quiere que el vendedor sepa de esta lectura (además de los avisos). */
  notas_ia: z.string().max(2_000).nullable(),
  /** Etapa 4: "revisa, autoriza o corrige la tabla antes de calcular" (Diego, 12-oct). null = leída y POR AUTORIZAR: el
   *  servidor no calcula con ella hasta que el vendedor la autorice. Ausente = lectura de antes de la etapa 4 o capturada
   *  a mano (ya revisada por quien la escribió): se calcula como siempre. */
  autorizada: AutorizacionLectura.nullable().optional(),
});
export type LecturaRecibo = z.infer<typeof LecturaRecibo>;

/** ¿Esta lectura espera que el vendedor la autorice? (leída de recibos, con datos, y sin autorizar). */
export function lecturaPorAutorizar(l: LecturaRecibo | null | undefined): boolean {
  return !!l && l.autorizada === null && l.historial.length > 0;
}

export const Lecturas = z.strictObject({ A: LecturaRecibo.nullable(), B: LecturaRecibo.nullable() });
export type Lecturas = z.infer<typeof Lecturas>;

/** Lista de cadenas privadas para el campo "privado.no_publicar" del JSON v1 (sin vacíos ni repetidos). */
export function cadenasPrivadas(lecturas: Lecturas, extra: readonly (string | null)[] = []): string[] {
  const todas: (string | null)[] = [...extra];
  for (const l of [lecturas.A, lecturas.B]) if (l) todas.push(...Object.values(l.privados));
  return [...new Set(todas.filter((s): s is string => typeof s === 'string' && s.trim().length >= 3).map((s) => s.trim()))];
}

const MESES_CFE_CORTOS = ['ENE', 'FEB', 'MAR', 'ABR', 'MAY', 'JUN', 'JUL', 'AGO', 'SEP', 'OCT', 'NOV', 'DIC'] as const;

/** Abreviaturas de mes que imprime CFE (también «SEPT» y «SET»). UN solo analizador para la lectura, la ventana del año y
 *  los meses sin recibo (verificación de la tanda 1, 25-oct: el de la ventana no entendía «SET» ni acentos). */
export const MESES_CFE: Readonly<Record<string, number>> = Object.freeze({
  ENE: 1, FEB: 2, MAR: 3, ABR: 4, MAY: 5, JUN: 6, JUL: 7, AGO: 8, SEP: 9, SEPT: 9, SET: 9, OCT: 10, NOV: 11, DIC: 12,
});

/** "AGO 26" → { anio: 2026, mes: 8 }. Acepta "AGO 2026", "ago-26", "SEPT. 25", "SET 25", "Díc 25". null si no es un mes. */
export function mesDeEtiqueta(etiqueta: string): { anio: number; mes: number } | null {
  const m = /^\s*([A-Za-zÁÉÍÓÚáéíóú]{3,4})\.?[\s-]*(\d{2}|\d{4})\s*$/.exec(etiqueta);
  if (!m) return null;
  const mes = MESES_CFE[m[1]!.toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')];
  if (!mes) return null;
  const n = Number(m[2]);
  return { anio: n < 100 ? 2000 + n : n, mes };
}

/** Mes «AAAA-MM» de un renglón del historial: el de su fecha final o, sin fechas (historial GDMT: «ABR 26», «SEPT 2025»),
 *  el de su etiqueta. null si no se sabe. */
export function mesDelRenglon(p: { fin: string | null; etiqueta: string }): string | null {
  if (p.fin && /^\d{4}-\d{2}-\d{2}$/.test(p.fin)) return p.fin.slice(0, 7);
  const m = mesDeEtiqueta(p.etiqueta);
  return m ? `${m.anio}-${String(m.mes).padStart(2, '0')}` : null;
}

/** «2026-04» → «ABR 26». */
export const etiquetaDeMes = (clave: string): string => `${MESES_CFE_CORTOS[Number(clave.slice(5, 7)) - 1] ?? '?'} ${clave.slice(2, 4)}`;

type RenglonVentana = { fin: string | null; etiqueta: string; origen?: string };
const numMes = (m: string) => Number(m.slice(0, 4)) * 12 + Number(m.slice(5, 7)) - 1;

/** Renglones que ANCLAN el año: los del recibo y los capturados a mano (los escribió el vendedor). Tanda 1c (verificador de
 *  la 1b): la lectura anclaba solo en recibos y el motor en cualquier renglón; un manual de SEP 26 tiraba en el motor el recibo
 *  real de SEP 25 mientras la tabla decía «no cuenta». UNA regla para lectura, tabla y motor: el sospechoso es solo el renglón
 *  del historial IMPRESO posterior al último recibo (casi siempre mal leído). */
export const anclaElAnio = (p: { origen?: string }): boolean => p.origen === 'recibo' || p.origen === 'manual';

/** El mes que ANCLA el año que se cotiza: el del recibo (o renglón manual) más reciente. Verificación de la tanda 1 (25-oct):
 *  anclado en cualquier renglón, un renglón del historial mal leído con fecha futura («OCT 26» por «OCT 25») tiraba recibos
 *  reales, y con «OCT 27» quedaban 1 renglón y 0 recibos. Con `hoy` («AAAA-MM-DD»), un recibo de un mes posterior a hoy (año
 *  mal leído) NO ancla (tanda 1c). Sin NINGÚN recibo ni manual con mes legible se usa el más reciente de todos. null si ningún
 *  renglón tiene mes. */
export function anclaDelAnio(historial: readonly RenglonVentana[], hoy: string | null = null): string | null {
  const meses = historial.map((p) => mesDelRenglon(p));
  const tope = hoy && /^\d{4}-\d{2}/.test(hoy) ? numMes(hoy.slice(0, 7)) : Infinity;
  const anclan = meses.filter((m, i): m is string => m !== null && anclaElAnio(historial[i]!));
  const todos = meses.filter((m): m is string => m !== null);
  const hastaHoy = (xs: string[]) => xs.filter((m) => numMes(m) <= tope);
  const base = [hastaHoy(anclan), anclan, hastaHoy(todos), todos].find((xs) => xs.length) ?? [];
  return base.length ? base.reduce((a, b) => (numMes(b) > numMes(a) ? b : a)) : null;
}

/** Diego, 25-oct («La Cacharamba», GDMTH con 11 recibos): la tabla de la revisión decía «26 periodos · 716,876 kWh en
 *  total» porque CADA recibo GDMT imprime su propio año de historial y, juntos, 11 recibos cubrían casi dos años. El año
 *  que se cotiza es la VENTANA, anclada en el recibo más reciente (anclaDelAnio): mensual, los 12 meses de calendario que
 *  terminan en ese mes; bimestral, los bimestres que terminan a menos de 335 días de él (6; la regla del 13-oct). Devuelve
 *  los índices que caen DENTRO (en su orden), los más viejos que quedan FUERA y los POSTERIORES al último recibo (renglón
 *  del historial o manual con un mes después del recibo más reciente: casi siempre mal leído; nunca mueve la ventana, no
 *  cuenta y se le dice al vendedor). Sin facturación conocida no se recorta, y un renglón sin mes legible se queda (no se
 *  sabe dónde cae: lo revisa el vendedor). */
export function ventanaDelAnio(historial: readonly RenglonVentana[], facturacion: 'mensual' | 'bimestral' | null, hoy: string | null = null): { dentro: number[]; fuera: number[]; posteriores: number[] } {
  const todos = historial.map((_, i) => i);
  const ancla = facturacion === null ? null : anclaDelAnio(historial, hoy);
  if (ancla === null) return { dentro: todos, fuera: [], posteriores: [] };
  const meses = historial.map((p) => mesDelRenglon(p));
  const posterior = (i: number) => meses[i] !== null && numMes(meses[i]!) > numMes(ancla);
  let viejo: (i: number) => boolean;
  if (facturacion === 'mensual') {
    viejo = (i) => meses[i] !== null && numMes(ancla) - numMes(meses[i]!) > 11;
  } else {
    // Bimestral: por días (el fin del renglón o el día 15 de su mes), como el corte que ya hacía la lectura desde el 13-oct.
    const dia = (i: number) => {
      const f = historial[i]!.fin;
      return Date.parse(`${f && /^\d{4}-\d{2}-\d{2}$/.test(f) ? f : `${meses[i]}-15`}T12:00:00Z`);
    };
    const delMes = todos.filter((i) => meses[i] === ancla);
    const delAncla = delMes.some((i) => anclaElAnio(historial[i]!)) ? delMes.filter((i) => anclaElAnio(historial[i]!)) : delMes;
    const ultimo = Math.max(...delAncla.map(dia));
    viejo = (i) => meses[i] !== null && !posterior(i) && (ultimo - dia(i)) / 86_400_000 >= 335;
  }
  return {
    dentro: todos.filter((i) => !posterior(i) && !viejo(i)),
    fuera: todos.filter((i) => !posterior(i) && viejo(i)),
    posteriores: todos.filter(posterior),
  };
}

/** Cuántos periodos DISTINTOS hay en esos renglones: un mes partido en dos renglones (cambio de medidor en GDMT) es UN mes
 *  (tanda 1c: la tabla decía «13 periodos» con 12 meses). Un renglón sin mes legible cuenta solo. */
export function periodosDistintos(historial: readonly RenglonVentana[], indices: readonly number[]): number {
  return new Set(indices.map((i) => mesDelRenglon(historial[i]!) ?? `#${i}`)).size;
}

/** La frase del aviso de la lectura que dice qué periodos viejos se quitaron (lo emite consolidar). Con ella la Revisión lo
 *  enseña JUNTO a la tabla, además de en los avisos (tanda 1c: era «info» y solo se veía en «Ver todos»). */
export const FRASE_PERIODOS_QUITADOS = 'que el año que se cotiza: cada recibo de CFE imprime su propio historial';

/** ¿Es el aviso (abierto) de los periodos viejos que la lectura quitó de la tabla de ESE medidor? */
export function esAvisoDePeriodosQuitados(a: { codigo: string; campo?: string | null; mensaje: string; resuelto?: boolean }, medidor: 'A' | 'B'): boolean {
  return !a.resuelto && a.codigo === 'OTRO' && a.campo === `/lecturas/${medidor}/historial` && a.mensaje.includes(FRASE_PERIODOS_QUITADOS);
}

/** Recibos que CUENTAN para «N de 12» (Recibos y el aviso del sistema): los subidos menos los que la última lectura dejó
 *  fuera del año que se cotiza (estado «fuera_del_anio»). Verificación de la tanda 1: Recibos decía «12 de 12» mientras
 *  Revisión decía «11 de 12». */
export function recibosQueCuentan(recibos: readonly { id: string }[], leidos: readonly { archivo_id: string; estado: string }[] | undefined): { cuentan: number; fuera: number } {
  const fueraIds = new Set((leidos ?? []).filter((l) => l.estado === 'fuera_del_anio').map((l) => l.archivo_id));
  const fuera = recibos.filter((r) => fueraIds.has(r.id)).length;
  return { cuentan: recibos.length - fuera, fuera };
}

// ───────────── Tanda 2: el mes que falta, PROYECTADO CON IA y MARCADO ─────────────

/** Desde cuántos meses con su propio recibo (o capturados a mano) dentro del año la IA proyecta los que faltan: con menos, el
 *  patrón no es confiable y se queda la estimación de siempre (historial impreso y perfil de Sonora). Así un GDMTO con UN
 *  recibo (lo normal: «con uno basta») no cambia. El 6 LO DECIDE DIEGO (verificación de la tanda 2, 25-oct): no se cambia
 *  sin su OK escrito. */
export const MINIMO_RECIBOS_PARA_PROYECTAR = 6;

/** Frase con que empieza (tras el prefijo del medidor) el aviso de los meses que la IA NO pudo proyectar (proyectar.ts). */
export const FRASE_PROYECCION_FALLIDA = 'La IA no pudo proyectar';
const CODIGOS_PROYECCION_FALLIDA: readonly string[] = ['IA_ERROR', 'IA_SIN_LLAVE', 'IA_TOPE_GASTO', 'IA_RECHAZO'];

/** ¿Es el aviso (abierto) de un mes que la IA no pudo proyectar en ESE medidor (caída, sin llave, tope de gasto, se negó o
 *  cifras fuera del patrón)? Revisión lo enseña JUNTO a la tabla, como la nota de «Proyectado con IA»: nunca solo en
 *  «Ver todos» (verificación de la tanda 2). */
export function esAvisoDeProyeccionFallida(a: { codigo: string; campo?: string | null; mensaje: string; resuelto?: boolean }, medidor: 'A' | 'B'): boolean {
  return !a.resuelto && CODIGOS_PROYECCION_FALLIDA.includes(a.codigo) && a.campo === `/lecturas/${medidor}/historial` && a.mensaje.includes(FRASE_PROYECCION_FALLIDA);
}

/** ¿Este renglón lo proyectó la IA y sigue como lo dejó? (si el vendedor lo editó, queda «manual» y ya es suyo). */
export const esProyectadoConIA = (p: { origen: string; proyeccion_ia?: ProyeccionIA | null }): boolean =>
  !!p.proyeccion_ia && (p.origen === 'estimado' || p.origen === 'historial_recibo');

/** Etiquetas («MAR 26») de los meses proyectados con IA, sin repetir. */
export function mesesProyectados(historial: readonly PeriodoConsumo[]): string[] {
  return [...new Set(historial.filter(esProyectadoConIA).map((p) => { const m = mesDelRenglon(p); return m ? etiquetaDeMes(m) : p.etiqueta; }))];
}

export interface MesPorProyectar {
  /** «AAAA-MM». */
  mes: string;
  etiqueta: string;
  /** Índice del renglón del historial impreso de ese mes (base «historial»), o null si no hay ninguno (base «patron»). */
  indice: number | null;
  base: ProyeccionIA['base'];
}

const moverMes = (mes: string, d: number) => new Date(Date.UTC(Number(mes.slice(0, 4)), Number(mes.slice(5, 7)) - 1 + d, 1)).toISOString().slice(0, 7);

/** Los meses del año que se cotiza (la MISMA ventana de la tanda 1c, anclada en el recibo más reciente) que la IA debe
 *  proyectar: GDMTH/GDMTO mensual, con al menos MINIMO_RECIBOS_PARA_PROYECTAR meses con recibo. Un mes con su renglón del
 *  historial impreso se proyecta solo en GDMTH (le falta el reparto por horario; en GDMTO el historial ya trae lo que se
 *  usa). Un mes partido en dos renglones o con un renglón manual no se toca (lo revisa el vendedor). */
export function mesesPorProyectar(l: { tarifa: string | null; facturacion: string | null; historial: readonly PeriodoConsumo[] }, hoy: string | null = null): MesPorProyectar[] {
  if ((l.tarifa !== 'GDMTH' && l.tarifa !== 'GDMTO') || l.facturacion !== 'mensual') return [];
  const ancla = anclaDelAnio(l.historial, hoy);
  if (!ancla) return [];
  const { dentro } = ventanaDelAnio(l.historial, 'mensual', hoy);
  const meses = l.historial.map((p) => mesDelRenglon(p));
  const conRecibo = new Set(dentro.filter((i) => anclaElAnio(l.historial[i]!) && meses[i]).map((i) => meses[i]!));
  if (conRecibo.size < MINIMO_RECIBOS_PARA_PROYECTAR) return [];
  const out: MesPorProyectar[] = [];
  for (let k = 11; k >= 0; k--) {
    const mes = moverMes(ancla, -k);
    if (conRecibo.has(mes)) continue;
    const filas = dentro.filter((i) => meses[i] === mes);
    if (filas.length > 1) continue;
    const i = filas[0] ?? null;
    if (i !== null && (l.historial[i]!.origen !== 'historial_recibo' || l.tarifa === 'GDMTO')) continue;
    out.push({ mes, etiqueta: etiquetaDeMes(mes), indice: i, base: i === null ? 'patron' : 'historial' });
  }
  return out;
}

/** Lo que se proyecta de un mes (la IA real o la simulada). */
export interface ValoresProyectados {
  consumo_kwh: number;
  demanda_kw: number | null;
  kwh_base: number | null;
  kwh_intermedia: number | null;
  kwh_punta: number | null;
  demanda_punta_kw: number | null;
}

const mediana = (xs: number[]) => {
  const o = [...xs].sort((a, b) => a - b);
  return o.length ? (o.length % 2 ? o[(o.length - 1) / 2]! : (o[o.length / 2 - 1]! + o[o.length / 2]!) / 2) : null;
};

/** Proyección SIN IA con el patrón de los otros meses del año (la usa la IA simulada y sirve de referencia para revisar la
 *  de la IA real): kWh, el promedio de los meses vecinos (o la mediana del año); reparto base/intermedia/punta, el promedio
 *  de los recibos; demanda de punta, la proporción promedio contra la demanda. Con base «historial», sus kWh y su demanda. */
export function proyeccionPorPatron(l: { tarifa: string | null; facturacion: string | null; historial: readonly PeriodoConsumo[] }, m: MesPorProyectar, hoy: string | null = null): ValoresProyectados {
  const { dentro } = ventanaDelAnio(l.historial, 'mensual', hoy);
  const otros = dentro.map((i) => l.historial[i]!).filter((p) => mesDelRenglon(p) !== m.mes && p.consumo_kwh > 0);
  const delMes = (d: number) => otros.filter((p) => mesDelRenglon(p) === moverMes(m.mes, d));
  const h = m.indice === null ? null : l.historial[m.indice]!;
  const vecinos = [...delMes(-1), ...delMes(1)];
  const prom = (xs: (number | null)[]) => { const v = xs.filter((x): x is number => x !== null); return v.length ? v.reduce((a, b) => a + b, 0) / v.length : null; };
  const consumo = h ? h.consumo_kwh : Math.round(prom(vecinos.map((p) => p.consumo_kwh)) ?? mediana(otros.map((p) => p.consumo_kwh)) ?? 0);
  const demanda = h ? h.demanda_kw : (() => { const d = prom(vecinos.map((p) => p.demanda_kw)) ?? mediana(otros.map((p) => p.demanda_kw).filter((x): x is number => x !== null)); return d === null ? null : Math.round(d * 10) / 10; })();
  if (l.tarifa !== 'GDMTH') return { consumo_kwh: consumo, demanda_kw: demanda, kwh_base: null, kwh_intermedia: null, kwh_punta: null, demanda_punta_kw: null };
  const conReparto = otros.filter((p) => p.kwh_base !== null && p.kwh_intermedia !== null && p.kwh_punta !== null && p.kwh_base + p.kwh_intermedia + p.kwh_punta > 0);
  const parte = (k: 'kwh_base' | 'kwh_punta') => prom(conReparto.map((p) => p[k]! / (p.kwh_base! + p.kwh_intermedia! + p.kwh_punta!)));
  const b = parte('kwh_base');
  const pu = parte('kwh_punta');
  const razon = prom(otros.filter((p) => p.demanda_punta_kw != null && p.demanda_kw).map((p) => p.demanda_punta_kw! / p.demanda_kw!));
  const reparto = b === null || pu === null ? null : repartoQueSuma(consumo, b, 1 - b - pu, pu);
  return { consumo_kwh: consumo, demanda_kw: demanda, ...(reparto ?? { kwh_base: null, kwh_intermedia: null, kwh_punta: null }), demanda_punta_kw: razon !== null && demanda !== null ? Math.round(demanda * razon * 10) / 10 : null };
}

/** Reparte `total` kWh en base/intermedia/punta con esas proporciones y que SUMEN exacto (el motor exige que cuadren). */
export function repartoQueSuma(total: number, b: number, i: number, p: number): { kwh_base: number; kwh_intermedia: number; kwh_punta: number } | null {
  const s = b + i + p;
  if (!(s > 0) || b < 0 || i < 0 || p < 0 || !(total >= 0)) return null;
  let kb = Math.round((total * b) / s);
  let kp = Math.round((total * p) / s);
  let ki = Math.round(total) - kb - kp;
  // Verificación de la tanda 2: con intermedia ≈ 0 los dos redondeos hacia arriba la dejaban en −1 (1000, 0.5005, 0,
  // 0.4995). Lo que falta (a lo más 1 kWh) sale de la franja mayor; el resto de los casos queda igual que antes.
  if (ki < 0) {
    if (kb >= kp) kb += ki;
    else kp += ki;
    ki = 0;
  }
  return { kwh_base: kb, kwh_intermedia: ki, kwh_punta: kp };
}

/** El historial con los meses proyectados puestos: base «historial», sobre su renglón (kWh, demanda, precio medio y factores
 *  de CFE intactos); base «patron», un renglón nuevo «estimado» en su lugar del año. Sin importe (lo estima el motor). */
export function conMesesProyectados(historial: readonly PeriodoConsumo[], proyectados: readonly { mes: MesPorProyectar; valores: ValoresProyectados; proyeccion: ProyeccionIA }[]): PeriodoConsumo[] {
  const out = historial.map((p) => ({ ...p }));
  for (const { mes, valores: v, proyeccion } of proyectados) {
    if (mes.indice !== null) {
      const h = out[mes.indice]!;
      const r = v.kwh_base !== null && v.kwh_intermedia !== null && v.kwh_punta !== null ? repartoQueSuma(h.consumo_kwh, v.kwh_base, v.kwh_intermedia, v.kwh_punta) : null;
      out[mes.indice] = { ...h, ...(r ?? {}), demanda_punta_kw: v.demanda_punta_kw ?? h.demanda_punta_kw ?? null, proyeccion_ia: proyeccion };
    }
  }
  for (const { mes, valores: v, proyeccion } of proyectados.filter((x) => x.mes.indice === null)) {
    const r = v.kwh_base !== null && v.kwh_intermedia !== null && v.kwh_punta !== null ? repartoQueSuma(v.consumo_kwh, v.kwh_base, v.kwh_intermedia, v.kwh_punta) : null;
    const fila: PeriodoConsumo = {
      inicio: null, fin: null, etiqueta: mes.etiqueta, consumo_kwh: Math.round(v.consumo_kwh), importe: null, demanda_kw: v.demanda_kw,
      kwh_base: r?.kwh_base ?? null, kwh_intermedia: r?.kwh_intermedia ?? null, kwh_punta: r?.kwh_punta ?? null,
      factor_potencia_pct: null, factor_carga_pct: null, precio_medio: null, origen: 'estimado', demanda_punta_kw: v.demanda_punta_kw, proyeccion_ia: proyeccion,
    };
    const antes = out.findIndex((p) => { const m = mesDelRenglon(p); return m !== null && m > mes.mes; });
    out.splice(antes === -1 ? out.length : antes, 0, fila);
  }
  return out;
}
